import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {PhotoLocation} from "../types/protocols";
import LocationPin from "./LocationPin";
import {useZoomPan} from "../hooks/useZoomPan";

interface PhotoOverlayProps {
    src: string;
    photoLocation: PhotoLocation;
    onClose: () => void;
}

const PhotoOverlay: React.FC<PhotoOverlayProps> = ({src, photoLocation, onClose}) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const transformRef = useRef<HTMLDivElement>(null);
    const [imgSize, setImgSize] = useState({width: 0, height: 0});

    // Ref to break the circular dependency: handleClick needs resetZoom, hook needs handleClick.
    const resetZoomRef = useRef<() => void>(() => {});

    const handleClick = useCallback((target: HTMLElement) => {
        if (target.closest('.overlay-image-container')) {
            resetZoomRef.current();
        } else {
            onClose();
        }
    }, [onClose]);

    const {zoom, pan, isDragging, handlers, resetZoom} = useZoomPan({
        maxZoom: 10,
        wheelZoomFactor: 1.3,
        doubleClickZoomFactor: 2,
        wheelElementRef: overlayRef,
        focalPointRef: containerRef,
        transformRef,
        ignoreSelector: '.overlay-close-btn',
        onClick: handleClick,
    });
    resetZoomRef.current = resetZoom;

    // ESC key to close
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // Lock body scroll while overlay is open
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    // Track overlay image size on resize (e.g. tablet rotation)
    useEffect(() => {
        const img = imgRef.current;
        if (!img) return;

        const observer = new ResizeObserver(() => {
            setImgSize({width: img.clientWidth, height: img.clientHeight});
        });
        observer.observe(img);
        return () => observer.disconnect();
    }, []);

    const markerPosition = useMemo(() => {
        if (!photoLocation || !imgSize.width) return null;
        const scale = imgSize.width / photoLocation.width;
        return {
            left: `${photoLocation.x * scale}px`,
            top: `${photoLocation.y * scale}px`,
        };
    }, [photoLocation, imgSize]);

    return createPortal(
        <div
            ref={overlayRef}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 10000,
                background: 'rgba(0, 0, 0, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isDragging ? 'grabbing' : 'grab',
            }}
            {...handlers}
        >
            {/* Close button */}
            <button
                className="overlay-close-btn"
                onClick={onClose}
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    width: '40px',
                    height: '40px',
                    border: 'none',
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10001,
                }}
                title="Stäng"
            >
                ✕
            </button>

            {/* Photo + marker container with zoom/pan */}
            <div
                ref={containerRef}
                className="overlay-image-container"
                style={{
                    position: 'relative',
                    display: 'inline-block',
                    overflow: 'visible',
                    userSelect: 'none',
                }}
            >
                <div
                    ref={transformRef}
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: '0 0',
                        transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                        position: 'relative',
                    }}
                >
                    <img
                        ref={imgRef}
                        src={src}
                        alt="Photo with marker"
                        onLoad={() => {
                            if (imgRef.current) {
                                setImgSize({
                                    width: imgRef.current.clientWidth,
                                    height: imgRef.current.clientHeight,
                                });
                            }
                        }}
                        style={{
                            maxWidth: '90vw',
                            maxHeight: '85vh',
                            objectFit: 'contain',
                            display: 'block',
                            borderRadius: '4px',
                            pointerEvents: 'none',
                        }}
                    />
                    {markerPosition && (
                        <LocationPin
                            left={markerPosition.left}
                            top={markerPosition.top}
                            scale={1 / zoom}
                        />
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PhotoOverlay;
